import React, { FC, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import {
  Modal,
  Button,
  Paragraph,
  Flex,
  Card,
  Heading,
  Svg,
  Show,
} from '@wunder-mobility/ui-components';
import { format } from 'date-fns';
import { Subscription } from '@/features/subscriptions/types/Subscription.types';
import { useRequestSwitchSubscriptionMutation } from '@/features/subscriptions/services/Subscription.service';
import { getBillingCycleLabel } from '@/features/subscriptions/utils/subscriptionHelpers';
import useSubscriptionSwitchPolicy from '@/features/subscriptions/hooks/useSubscriptionSwitchPolicy';
import SubscriptionBenefits from '@/features/subscriptions/components/SubscriptionOverview/SubscriptionBenefits';
import CurrencyDisplay from '@/core/components/CurrencyDisplay';
import { DateFormats } from '@/core/enums';
import styles from '@/features/subscriptions/assets/scss/SwitchSubscriptionModal.module.scss';

interface SwitchSubscriptionModalProps {
  currentSubscription: Subscription;
  targetSubscription: Subscription;
  onCancel: () => void;
}

const SwitchSubscriptionModal: FC<SwitchSubscriptionModalProps> = ({
  currentSubscription,
  targetSubscription,
  onCancel,
}) => {
  // The end-user chooses when the switch takes effect (mirrors mobile), but the
  // "switch immediately" option is only offered when the operator allows it (BUS-2482).
  const { allowImmediateSwitch, isLoading: isPolicyLoading } = useSubscriptionSwitchPolicy();
  const [immediateSelected, setImmediateSelected] = useState<boolean>(true);
  const [switchSubscription, { isLoading }] = useRequestSwitchSubscriptionMutation();

  // When immediate switching is disallowed, always submit a next-renewal switch.
  const switchImmediately = allowImmediateSwitch && immediateSelected;

  const cycleLabel = getBillingCycleLabel(targetSubscription.interval);

  const nextRenewalDate = currentSubscription.validUntil
    ? format(new Date(currentSubscription.validUntil), DateFormats.SHORT_DATE)
    : null;

  const handleSwitch = async () => {
    if (currentSubscription.id == null) {
      return;
    }
    try {
      await switchSubscription({
        id: currentSubscription.id,
        targetSubscriptionCode: targetSubscription.code,
        switchImmediately,
      }).unwrap();
      onCancel?.();
    } catch (error) {
      return error;
    }
  };

  return (
    <Modal
      opened
      onClose={onCancel}
      variation="small"
      modalHeader={<Trans id="subscriptions.switch.title">Switch Plan</Trans>}
      modalFooter={
        <Flex direction="column" gap="md" width="full">
          <Paragraph size={5} textColor="text-400" marginBottom={0} align="center">
            <Show when={switchImmediately}>
              <Trans id="subscriptions.switch.disclaimer.immediate">
                Your plan changes right away and billing is adjusted accordingly.
              </Trans>
            </Show>
            <Show when={!switchImmediately && !!nextRenewalDate}>
              <Trans id="subscriptions.switch.disclaimer.nextRenewal">
                Your current plan stays active until {nextRenewalDate}, then switches automatically.
              </Trans>
            </Show>
            <Show when={!switchImmediately && !nextRenewalDate}>
              <Trans id="subscriptions.switch.disclaimer.nextRenewalNoDate">
                Your current plan stays active until it expires, then switches automatically.
              </Trans>
            </Show>
          </Paragraph>

          <Button
            variation="primary"
            onClick={() => {
              handleSwitch();
            }}
            loading={isLoading || isPolicyLoading}
            disabled={isLoading || isPolicyLoading || currentSubscription.id == null}
            width="full"
            size="large"
          >
            <Trans id="subscriptions.switch.confirm">Confirm Switch</Trans>
          </Button>
        </Flex>
      }
    >
      <Flex direction="column" gap="lg">
        <Paragraph size={4} textColor="text-400" marginBottom={0}>
          <Trans id="subscriptions.switch.from">
            Switching from {currentSubscription.description || currentSubscription.code}
          </Trans>
        </Paragraph>

        <Card className={styles['switch-subscription-modal__subscription']}>
          <Flex gap="md" align="flex-start">
            <Svg icon="card" width={32} height={32} />
            <Flex direction="column" width="full">
              <Heading size={6} marginBottom={4}>
                {targetSubscription.description || targetSubscription.code}
              </Heading>
              <Flex align="flex-end">
                <Heading size={5}>
                  <CurrencyDisplay
                    value={targetSubscription.price}
                    currency={targetSubscription.currency}
                  />
                </Heading>
                <Paragraph size={4} textColor="text-400" marginBottom={12}>
                  {cycleLabel}
                </Paragraph>
              </Flex>
              <SubscriptionBenefits subscription={targetSubscription} />
            </Flex>
          </Flex>
        </Card>

        {allowImmediateSwitch && (
          <Flex direction="column" gap="sm">
            <Paragraph size={3} marginBottom={0}>
              <Trans id="subscriptions.switch.whenLabel">When should the switch happen?</Trans>
            </Paragraph>

            <Button
              width="full"
              variation={immediateSelected ? 'primary' : 'secondary'}
              onClick={() => setImmediateSelected(true)}
            >
              <Trans id="subscriptions.switch.option.immediate">Switch immediately</Trans>
            </Button>
            <Button
              width="full"
              variation={!immediateSelected ? 'primary' : 'secondary'}
              onClick={() => setImmediateSelected(false)}
            >
              {nextRenewalDate ? (
                <Trans id="subscriptions.switch.option.nextRenewalDated">
                  At next renewal ({nextRenewalDate})
                </Trans>
              ) : (
                <Trans id="subscriptions.switch.option.nextRenewal">At next renewal</Trans>
              )}
            </Button>
          </Flex>
        )}
      </Flex>
    </Modal>
  );
};

export default SwitchSubscriptionModal;
